/**
 * UpgradeHistoryPanel
 *
 * Displays Premium → Family upgrade history in the admin user profile.
 *
 * Sub-tasks:
 *   5.1 — Subscription event history: subscription_upgraded events with
 *          fromPlan, toPlan, remainingDays, carryOverEndDate
 *   5.2 — Family Annual baseEndDate and nextBillingDate for upgraded users
 *   5.3 — "Upgraded from Premium" badge when upgrade events exist
 *
 * References: Req 10
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_ENDPOINTS, buildApiUrl } from '../../config/api-endpoints';
import './UpgradeHistoryPanel.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (isoStr) => {
  if (!isoStr) return 'N/A';
  return new Date(isoStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Singapore',
  });
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Singapore',
  });
};

const PLAN_LABELS = {
  'premium-monthly': 'Premium Monthly',
  'premium-annual': 'Premium Annual',
  'family-annual': 'Family Annual',
  'family-monthly': 'Family Monthly',
};

const planLabel = (planId) => PLAN_LABELS[planId] || planId || 'N/A';

// ─── Sub-components ───────────────────────────────────────────────────────────

/** 5.3 — "Upgraded from Premium" badge */
const UpgradedBadge = () => (
  <span className="upgrade-history__badge">
    ⬆️ Upgraded from Premium
  </span>
);

/** 5.2 — Family Annual billing dates */
const FamilyBillingDates = ({ subscription }) => {
  if (!subscription) return null;

  return (
    <div className="admin-card upgrade-history__billing-card">
      <div className="admin-card-header">
        <h3 className="admin-card-title">👨‍👩‍👧 Active Family Annual Subscription</h3>
      </div>
      <div className="user-detail__info-grid">
        <div className="user-detail__info-item">
          <label>Plan</label>
          <span>{planLabel(subscription.planId)}</span>
        </div>
        <div className="user-detail__info-item">
          <label>Status</label>
          <span>{subscription.status || 'N/A'}</span>
        </div>
        <div className="user-detail__info-item">
          <label>Base End Date</label>
          <span>{formatDate(subscription.baseEndDate)}</span>
        </div>
        <div className="user-detail__info-item">
          <label>Next Billing Date</label>
          <span>{formatDate(subscription.nextBillingDate)}</span>
        </div>
        {subscription.paymentIntentId && (
          <div className="user-detail__info-item">
            <label>Payment Intent ID</label>
            <span className="user-detail__mono">
              ...{subscription.paymentIntentId.slice(-10)}
            </span>
          </div>
        )}
        {subscription.promoExtensionDays > 0 && (
          <div className="user-detail__info-item">
            <label>Promo Extension Days</label>
            <span>+{subscription.promoExtensionDays} days</span>
          </div>
        )}
      </div>
    </div>
  );
};

/** 5.1 — Upgrade event history table */
const UpgradeEventHistory = ({ events }) => {
  if (!events.length) {
    return (
      <div className="admin-empty">
        <div className="admin-empty-icon">📋</div>
        <div className="admin-empty-title">No upgrade events</div>
        <div className="admin-empty-text">No subscription_upgraded events found for this user.</div>
      </div>
    );
  }

  return (
    <div className="upgrade-history__events">
      {events.map((evt, i) => (
        <div key={evt.eventId || i} className="upgrade-history__event-item">
          <div className="upgrade-history__event-header">
            <span className="upgrade-history__event-type">⬆️ Subscription Upgraded</span>
            <span className="upgrade-history__event-time">{formatDateTime(evt.createdAt)}</span>
          </div>
          <div className="upgrade-history__event-details">
            <div className="upgrade-history__detail-row">
              <span className="upgrade-history__detail-label">From Plan</span>
              <span className="upgrade-history__detail-value">{planLabel(evt.fromPlan)}</span>
            </div>
            <div className="upgrade-history__detail-row">
              <span className="upgrade-history__detail-label">To Plan</span>
              <span className="upgrade-history__detail-value">{planLabel(evt.toPlan)}</span>
            </div>
            <div className="upgrade-history__detail-row">
              <span className="upgrade-history__detail-label">Days Carried Over</span>
              <span className="upgrade-history__detail-value upgrade-history__detail-value--highlight">
                {evt.remainingDays ?? 'N/A'} days
              </span>
            </div>
            <div className="upgrade-history__detail-row">
              <span className="upgrade-history__detail-label">Carry-Over End Date</span>
              <span className="upgrade-history__detail-value">{formatDate(evt.carryOverEndDate)}</span>
            </div>
            {evt.paymentIntentId && (
              <div className="upgrade-history__detail-row">
                <span className="upgrade-history__detail-label">Payment Intent</span>
                <span className="upgrade-history__detail-value user-detail__mono">
                  ...{evt.paymentIntentId.slice(-10)}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UpgradeHistoryPanel = ({ user }) => {
  const [upgradeEvents, setUpgradeEvents] = useState([]);
  const [activeFamilySubscription, setActiveFamilySubscription] = useState(null);
  const [hasUpgraded, setHasUpgraded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.userId;

  const fetchUpgradeHistory = useCallback(async () => {
    if (!API_ENDPOINTS.ADMIN.FAMILY_UPGRADE_HANDLER) {
      setError('Family upgrade handler URL not configured. Set VITE_FAMILY_UPGRADE_HANDLER_URL to enable this panel.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const url = buildApiUrl(
        API_ENDPOINTS.ADMIN.FAMILY_UPGRADE_HANDLER,
        `/admin/upgrade-history?userId=${encodeURIComponent(userId)}`
      );

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error?.message || `Request failed (${response.status})`);
      }

      setUpgradeEvents(data.upgradeEvents || []);
      setActiveFamilySubscription(data.activeFamilySubscription || null);
      setHasUpgraded(data.hasUpgraded || false);
    } catch (err) {
      console.error('[UpgradeHistoryPanel] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchUpgradeHistory();
  }, [userId, fetchUpgradeHistory]);

  return (
    <div className="upgrade-history">
      {/* 5.3 — Badge + section header */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">
            📋 Upgrade History
            {hasUpgraded && <UpgradedBadge />}
          </h3>
        </div>

        {loading && (
          <div className="upgrade-history__loading">Loading upgrade history...</div>
        )}

        {error && (
          <div className="admin-alert admin-alert--warning upgrade-history__alert">
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && (
          <div className="upgrade-history__body">
            <UpgradeEventHistory events={upgradeEvents} />
          </div>
        )}
      </div>

      {/* 5.2 — Family Annual billing dates (only shown when upgraded) */}
      {!loading && !error && hasUpgraded && (
        <FamilyBillingDates subscription={activeFamilySubscription} />
      )}
    </div>
  );
};

export default UpgradeHistoryPanel;
